import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import type { UseTiptapProps } from '@/types';

import { cn } from '@/lib/utils';
import { EditorFixes } from '@/utils/helpers/tiptap-helpers';

export const useTipTap = ({ value, onChange }: UseTiptapProps) => {
  const { t } = useTranslation();

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      EditorFixes,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'underline underline-offset-4',
        },
      }),
      Placeholder.configure({
        placeholder: t('placeholder.enter_description'),
      }),
    ],
    content: value || '',
    editorProps: {
      attributes: {
        class: cn(
          'p-3 h-full outline-none cursor-text',
          'prose prose-sm max-w-none prose-p:my-1 prose-ul:my-1 prose-ol:my-1',
          '[&_ul]:list-disc! [&_ul]:pl-6! [&_ul_li::marker]:text-black!',
          '[&_ol]:list-decimal! [&_ol]:pl-6! [&_ol_li::marker]:text-black!',
          '[&_em]:italic',
          '[&_em]:[font-synthesis:style]!'
        ),
      },
    },
    onUpdate: ({ editor }) => {
      onChange?.(editor.getHTML());
    },
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || '');
    }
  }, [value, editor]);

  return editor;
};
